<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'

const route = useRoute()
const incidentId = route.params.id as string

const incidentStore = useIncidentStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const incident = computed(() => incidentStore.currentIncident)
const canEdit = computed(() => permissionsStore.can('incident.edit', opRole.value))
const isClosed = computed(() => incident.value?.status === 'closed')

const editing = ref(false)
const draft = ref('')
const textarea = ref<HTMLTextAreaElement | null>(null)
const saving = ref(false)

async function startEdit() {
  if (!canEdit.value || isClosed.value) return
  draft.value = incident.value?.situation_summary ?? ''
  editing.value = true
  await nextTick()
  textarea.value?.focus()
  textarea.value?.select()
}

async function save() {
  if (saving.value) return
  saving.value = true
  try {
    await incidentStore.updateIncident(incidentId, { situation_summary: draft.value.trim() || null })
  } finally {
    saving.value = false
    editing.value = false
  }
}

function cancel() {
  editing.value = false
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') cancel()
}
</script>

<template>
  <div class="bg-surface border border-border rounded-lg p-4">
    <div class="flex items-center justify-between mb-3">
      <span class="text-[10px] font-label uppercase tracking-widest text-outline">
        Situation Summary
      </span>
      <button
        v-if="canEdit && !isClosed && !editing"
        class="p-1 text-outline hover:text-on-surface transition-colors rounded"
        title="Edit description"
        @click="startEdit"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>
    </div>

    <!-- Edit mode -->
    <div v-if="editing" class="flex flex-col gap-2">
      <textarea
        ref="textarea"
        v-model="draft"
        rows="4"
        class="w-full text-sm text-on-surface bg-background border border-border rounded px-2.5 py-2 resize-y focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-outline"
        placeholder="Describe the situation — circumstances, terrain, weather, known hazards…"
        @keydown="onKeydown"
      />
      <div class="flex items-center gap-2 justify-end">
        <button
          class="text-[11px] font-label uppercase tracking-wide text-outline hover:text-on-surface px-2 py-1 transition-colors"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          class="text-[11px] font-label uppercase tracking-wide text-primary hover:text-primary/80 px-2 py-1 transition-colors"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>

    <!-- View mode -->
    <template v-else>
      <p
        v-if="incident?.situation_summary"
        class="text-sm text-on-surface whitespace-pre-wrap leading-relaxed"
      >{{ incident.situation_summary }}</p>
      <p v-else class="text-sm text-outline italic">
        {{ canEdit && !isClosed ? 'No situation summary — click the pencil to add one.' : 'No situation summary added.' }}
      </p>
    </template>
  </div>
</template>
